import { z } from "zod";
import { type HelpCitationPassage, helpCitationSchema } from "./help.ts";
import { citationStatusSchema, messageCitationSchema, passageLocationSchema } from "./knowledge.ts";

/**
 * Cited answers (spec `cited-answers`, design §8): the contract between the API and every
 * client that renders an assistant message.
 *
 * **In the text.** The model cites a passage with its per-turn handle right after the sourced
 * statement, before the closing punctuation: `The torque is 45 Nm [S1].` Several handles are
 * written `[S1][S3]` (`[S1, S3]` is also accepted). A statement drawn from general knowledge or
 * reasoning ends with {@link UNSOURCED_MARKER}. Clients replace both tokens with badges.
 *
 * **The `data-citations` part.** Each assistant message carries one UI message part of type
 * {@link CITATIONS_PART_TYPE} with the ID {@link CITATIONS_PART_ID}, always last:
 * - while streaming, it is written once the text is complete with `state: "checking"` and every
 *   status `unchecked`, then replaced (same ID, AI SDK data-part reconciliation) with
 *   `state: "done"` and the statuses of the post-generation check;
 * - on reload (`GET …/conversations/:id`), it is rebuilt from the stored citations under the
 *   viewer's current access (`state: "done"`); a hidden message has no parts at all.
 *
 * **Classification.** An available citation carries its document's level id (`classification`)
 * and `aboveLowest` when that level is above the tenant scheme's lowest; both are read under the
 * viewer's current access with the citation. An unavailable citation never carries either.
 *
 * Opening a citation goes through `GET /api/projects/:projectId/documents/:documentId/download`
 * on every click: URLs are never part of a message.
 */

/** UI message part type of the citations data part (`data-` + {@link CITATIONS_DATA_NAME}). */
export const CITATIONS_DATA_NAME = "citations";
export const CITATIONS_PART_TYPE = `data-${CITATIONS_DATA_NAME}` as const;
/** One citations part per message: later writes with this ID replace it in place. */
export const CITATIONS_PART_ID = "citations";

/** Ends a statement that is not drawn from the project's sources. */
export const UNSOURCED_MARKER = "[unsourced]";

/**
 * A citation whose source the viewer can read now: the stored reference plus its title.
 * `kind: "wiki_page"` opens the cited version in the wiki instead of a download, and
 * `newerVersion` says a newer version of the document exists ("newer version available").
 * `generated`: the source was written by the agent (an artefact added to knowledge, or a file of
 * origin `generated`): shown "Generated by agent", never as a primary source. A flag rather than
 * a new `kind`, which the release before would fail to parse.
 */
export const availableCitationSchema = messageCitationSchema.extend({
  title: z.string(),
  kind: z.literal("wiki_page").optional(),
  newerVersion: z.literal(true).optional(),
  /**
   * An attachment of this conversation (conversation-attachments): its preview reads "This
   * conversation" and opening it goes through the attachment download. A separate marker, not a
   * `kind`, so a client one release behind strips it instead of refusing the citation.
   */
  attachment: z.literal(true).optional(),
  generated: z.literal(true).optional(),
  /**
   * The level id of the document's classification (tenant scheme), read with the citation under
   * the viewer's current access: never on an unavailable citation, nor on a web one. Shown as a
   * neutral mark on the source row and preview; an id the client's scheme lacks shows as is.
   */
  classification: z.string().min(1).max(64).optional(),
  /**
   * Set when `classification` is above the lowest level of the tenant scheme, computed by the
   * server so every viewer (guests too, who read no scheme) gets it: the compact chip's lock.
   */
  aboveLowest: z.literal(true).optional(),
});

/**
 * A citation whose source was deleted since, or that the viewer can no longer read: only the
 * handle and status, never the document ID or location, and no link. `deleted` is set when
 * the source was deleted and the viewer still passes its kept readers: render "Source
 * deleted"; otherwise "Source unavailable" (nothing says whether an unreadable source exists).
 */
export const unavailableCitationSchema = messageCitationSchema
  .pick({ handle: true, status: true })
  .extend({ title: z.null(), deleted: z.literal(true).optional() });

/** A citation as rendered for one viewer: narrow on `title === null`. */
export const renderedCitationSchema = z.union([availableCitationSchema, unavailableCitationSchema]);
export type RenderedCitation = z.infer<typeof renderedCitationSchema>;

/**
 * A statement shown as not drawn from project sources: it cites no handle, or ends with
 * {@link UNSOURCED_MARKER}. Offsets are UTF-16 code units into the text of the message's
 * `textPart`-th non-blank text part (0-based, counting only text parts whose text is not
 * whitespace: blank ones are never stored), `end` exclusive; markdown
 * headings and blank lines are never included.
 */
export const unsourcedRangeSchema = z.object({
  textPart: z.int().min(0),
  start: z.int().min(0),
  end: z.int().min(0),
});
export type UnsourcedRange = z.infer<typeof unsourcedRangeSchema>;

/**
 * A statement with at least one citation that failed the post-generation check (#324): its
 * sentence (same offsets as {@link unsourcedRangeSchema}) and the handles it cites that failed
 * there. The same handle may be supported in other statements.
 */
export const unverifiedRangeSchema = unsourcedRangeSchema.extend({
  handles: z.array(z.string().regex(/^[SWH]\d{1,9}$/)).min(1),
});
export type UnverifiedRange = z.infer<typeof unverifiedRangeSchema>;

/** A web citation's handle: `W1`, `W2`, … (per turn, numbered after earlier messages). */
export const webHandleSchema = z.string().regex(/^W\d{1,9}$/);

/** Whether `handle` is a web citation handle (any case). */
export const isWebHandle = (handle: string): boolean =>
  webHandleSchema.safeParse(handle.toUpperCase()).success;

/** The model-facing name of the web search tool (its UI parts are `tool-web_search`). */
export const WEB_SEARCH_TOOL_NAME = "web_search";

/**
 * A citation of a web search result (spec `cited-answers` "Web citations"): rendered apart from
 * knowledge citations, with a "web" badge, the domain and the published date when known. `url`
 * is always `http` or `https` (anything else was dropped before the model saw the result).
 * Public content: it is shown to whoever may view the message, with no document access check.
 */
export const webCitationSchema = z.object({
  handle: webHandleSchema,
  status: citationStatusSchema,
  url: z.url({ protocol: /^https?$/ }).max(2048),
  /** The URL's host name without a leading `www.`. */
  domain: z.string().min(1).max(253),
  title: z.string().max(300).nullable(),
  publishedAt: z.iso.datetime({ offset: true }).nullable(),
});
export type WebCitation = z.infer<typeof webCitationSchema>;

export const citationsDataSchema = z.object({
  /** `checking` until the post-generation check has run (or failed); then `done`. */
  state: z.enum(["checking", "done"]),
  /**
   * The handles cited in the text that resolve to a passage of the turn, in order of first
   * appearance. `status`: `supported` or `unsupported` (show "unverified") by the check, or
   * `unchecked` when it could not run. A handle the model invented is not a citation.
   */
  citations: z.array(renderedCitationSchema),
  /** Web citations (`W#`) cited in the text, in order of first appearance, with their status. */
  web: z.array(webCitationSchema).default([]),
  /**
   * Product help citations (`H#`) cited in the text, in order of first appearance: sourced
   * statements, rendered as "ESI help" chips. Absent from servers one release behind.
   */
  help: z.array(helpCitationSchema).default([]),
  unsourced: z.array(unsourcedRangeSchema),
  /**
   * Statements that cite only web results: shown "from the web", never as from project sources
   * nor as unsourced. Same offsets as `unsourced`.
   */
  fromWeb: z.array(unsourcedRangeSchema).default([]),
  /**
   * The check's verdicts per statement: each statement where a cited handle failed, with those
   * handles. A chip of that handle in that statement reads unverified; elsewhere it shows its
   * handle's status. Absent while checking, from servers one release behind and on messages
   * stored before it: clients then judge a statement by its handles' statuses.
   */
  unverified: z.array(unverifiedRangeSchema).optional(),
});
export type CitationsData = z.infer<typeof citationsDataSchema>;

/** The part as it appears in `UIMessage.parts`. */
export const citationsPartSchema = z.object({
  type: z.literal(CITATIONS_PART_TYPE),
  id: z.literal(CITATIONS_PART_ID),
  data: citationsDataSchema,
});
export type CitationsPart = z.infer<typeof citationsPartSchema>;

/** Most characters of passage text a preview carries (cut on a word, ending with "…"). */
export const MAX_PASSAGE_PREVIEW_CHARS = 1200;

/**
 * The preview of one cited passage (`GET /api/projects/:projectId/conversations/:conversationId/
 * messages/:messageId/citations/:handle`), read at display time under the viewer's current
 * access. `excerpt` is null with `versionChanged` when the document moved to a newer version
 * whose passages no longer include the cited one (show "updated since the answer"), and null
 * alone when the passage has no text to preview (an image). Any refusal is a plain `404`.
 */
export const citationPassageSchema = z.object({
  title: z.string(),
  location: passageLocationSchema,
  status: citationStatusSchema,
  excerpt: z
    .string()
    .max(MAX_PASSAGE_PREVIEW_CHARS + 1)
    .nullable(),
  versionChanged: z.boolean(),
  /**
   * The names of the document's folders from its section root (empty at the root), read live:
   * a rename shows on the next read (knowledge-folders). Absent from older servers.
   */
  path: z.array(z.string()).optional(),
  /**
   * An attachment of this conversation (conversation-attachments): the preview reads "This
   * conversation" in place of a folder path, and opening it goes through the attachment download.
   */
  attachment: z.literal(true).optional(),
});
export type CitationPassage = z.infer<typeof citationPassageSchema>;

/**
 * The preview of one web citation (same endpoint, `W#` handle): the snippet the agent received,
 * as stored with the message, and when it was retrieved. The page is never fetched again.
 */
export const webCitationPassageSchema = z.object({
  kind: z.literal("web"),
  url: webCitationSchema.shape.url,
  domain: webCitationSchema.shape.domain,
  title: webCitationSchema.shape.title,
  publishedAt: webCitationSchema.shape.publishedAt,
  retrievedAt: z.iso.datetime({ offset: true }),
  status: citationStatusSchema,
  excerpt: z.string().max(MAX_PASSAGE_PREVIEW_CHARS + 1),
});
export type WebCitationPassage = z.infer<typeof webCitationPassageSchema>;

/** What the citation preview endpoint returns: a knowledge passage, a web snippet or a help passage. */
export type CitationPreview = CitationPassage | WebCitationPassage | HelpCitationPassage;

/** The domain shown for a web citation: the host name without a leading `www.`. */
export function webDomainOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}
